'use client'

import React, { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  BookOpenIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/solid'
import Button from '@/components/Button'
import ValuesList from '@/data/valuesList.json'

const PILLAR_ICONS = [BookOpenIcon, ArrowTrendingUpIcon, SparklesIcon]

const NARRATIVE =
  "LAL was founded on a simple realization: the financial industry is designed to keep you dependent. We were built to break that cycle. By fusing high-level strategy with radical transparency, we created a methodology that doesn't just manage wealth—it multiplies confidence."

const OurStory = () => {
  const { valuesList } = ValuesList
  const containerRef = useRef(null)

  // Left column refs
  const badgeRef = useRef(null)
  const headlineRef = useRef(null)
  const narrativeRef = useRef(null)
  const ctaRef = useRef(null)

  // Right column refs
  const card1Ref = useRef(null)
  const card2Ref = useRef(null)
  const card3Ref = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (prefersReducedMotion) {
      gsap.set(
        [badgeRef.current, headlineRef.current, narrativeRef.current, ctaRef.current],
        { autoAlpha: 1, y: 0 }
      )
      gsap.set([card1Ref.current, card2Ref.current, card3Ref.current], {
        autoAlpha: 1,
        y: 0,
      })
      return
    }

    const ctx = gsap.context(() => {
      gsap.registerPlugin(ScrollTrigger)

      // Initial states
      gsap.set([badgeRef.current, headlineRef.current, narrativeRef.current, ctaRef.current], {
        y: 30,
        autoAlpha: 0,
      })
      gsap.set([card1Ref.current, card2Ref.current, card3Ref.current], {
        y: 40,
        autoAlpha: 0,
      })

      // Main content timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 70%',
          toggleActions: 'play none none none',
          once: true,
        },
      })

      tl.to(badgeRef.current, {
        y: 0,
        autoAlpha: 1,
        duration: 0.7,
        ease: 'power3.out',
      })
        .to(
          headlineRef.current,
          { y: 0, autoAlpha: 1, duration: 0.85, ease: 'power3.out' },
          '-=0.4'
        )
        .to(
          narrativeRef.current,
          { y: 0, autoAlpha: 1, duration: 0.75, ease: 'power2.out' },
          '-=0.5'
        )
        .to(
          ctaRef.current,
          { y: 0, autoAlpha: 1, duration: 0.6, ease: 'back.out(1.7)' },
          '-=0.4'
        )
        .to(
          [card1Ref.current, card2Ref.current, card3Ref.current],
          {
            y: 0,
            autoAlpha: 1,
            duration: 0.9,
            ease: 'power3.out',
            stagger: 0.12,
          },
          '-=0.6'
        )
    }, containerRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={containerRef}
      id="story"
      className="relative min-h-screen w-full overflow-hidden bg-primary grid grid-cols-1 lg-first:grid-cols-[45%_55%] gap-0"
      aria-labelledby="our-story-heading"
    >
      {/* --- BACKGROUND LAYERS (lighter so blueprint grid is visible) --- */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-gray-900/80 via-black/70 to-black/80 z-0 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent z-0 pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] z-0 pointer-events-none mix-blend-overlay" />

      {/* --- BLUEPRINT SCHEMATIC: Grid + Crosshairs (Engineering a Legacy) --- */}
      <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
        <svg
          className="absolute inset-0 w-full h-full opacity-60"
          viewBox="0 0 1400 900"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern
              id="blueprint-grid"
              width="80"
              height="80"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 0 0 L 80 0 M 0 0 L 0 80"
                fill="none"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#blueprint-grid)" />
          {/* Gold crosshairs at selected grid intersections */}
          {Array.from({ length: 12 }, (_, i) =>
            Array.from({ length: 18 }, (_, j) => {
              if ((i + j) % 3 !== 0) return null
              const x = 60 + j * 80
              const y = 60 + i * 80
              return (
                <g key={`${i}-${j}`}>
                  <line
                    x1={x - 4}
                    y1={y}
                    x2={x + 4}
                    y2={y}
                    stroke="rgba(223,175,55,0.5)"
                    strokeWidth="1"
                  />
                  <line
                    x1={x}
                    y1={y - 4}
                    x2={x}
                    y2={y + 4}
                    stroke="rgba(223,175,55,0.5)"
                    strokeWidth="1"
                  />
                </g>
              )
            })
          ).flat().filter(Boolean)}
        </svg>
        {/* Lighter gradient so we can see the grid */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/60" />
      </div>

      {/* --- LEFT COLUMN: NARRATIVE --- */}
      <div
        className="relative flex flex-col px-4 sm:px-6 lg-first:px-12 min-[1575px]:px-16 py-16 lg-first:py-24 z-10 lg-first:sticky lg-first:top-32 lg-first:self-start"
      >
        <div className="flex-1 flex flex-col justify-center">
          <span
            ref={badgeRef}
            className="mb-4 inline-block w-fit rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-300"
          >
            Our Story
          </span>
          <h2
            id="our-story-heading"
            ref={headlineRef}
            className="font-montserrat font-bold text-4xl sm:text-5xl lg-first:text-6xl text-white tracking-tight mb-6"
          >
            BUILT TO <span className="empire-text">TRANSFORM.</span>
          </h2>
          <p
            ref={narrativeRef}
            className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-xl mb-8 lg-first:mb-10"
          >
            {NARRATIVE}
          </p>
          <div ref={ctaRef}>
            <a href="#contact" className="inline-block group">
              <Button
                variant="bg-custom"
                className="relative flex items-center px-8 py-4 text-sm font-bold uppercase tracking-widest rounded-full gap-3 text-black bg-gradient-to-r from-secondary via-[#F4E080] to-secondary shadow-xl hover:scale-105 transition-transform duration-300"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Your Journey
                  <ArrowRightIcon className="w-4 h-4" />
                </span>
              </Button>
            </a>
          </div>
        </div>

        {/* --- The "Gap Filler": Animated Compass SVG --- */}
        <div className="mt-auto pt-12 opacity-60 flex justify-center text-white" aria-hidden="true">
          <div className="w-52 h-52 sm:w-64 sm:h-64 lg-first:w-72 lg-first:h-72">
            <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible" fill="none">
              {/* Outer Ring (Slow Rotate) */}
              <g className="animate-spin-20s" style={{ transformOrigin: '50px 50px' }}>
                <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-white/20" />
                <path d="M50 2 L50 10 M50 90 L50 98 M2 50 L10 50 M90 50 L98 50" stroke="currentColor" strokeWidth="1" className="text-white/30" />
              </g>

              {/* Inner Ring (Reverse Rotate) */}
              <g className="animate-spin-reverse-15s" style={{ transformOrigin: '50px 50px' }}>
                <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-white/20" strokeDasharray="4 4" />
              </g>

              {/* Core (Gold Accent) */}
              <circle cx="50" cy="50" r="15" fill="none" stroke="#DFAF37" strokeWidth="1" strokeOpacity="0.4" />
              <circle cx="50" cy="50" r="3" fill="#DFAF37" className="animate-pulse" />

              {/* Decorative Lines */}
              <line x1="50" y1="20" x2="50" y2="80" stroke="#DFAF37" strokeWidth="0.5" strokeOpacity="0.3" />
              <line x1="20" y1="50" x2="80" y2="50" stroke="#DFAF37" strokeWidth="0.5" strokeOpacity="0.3" />
            </svg>
          </div>
        </div>
      </div>

      {/* --- RIGHT COLUMN: VALUES STACK --- */}
      <div className="relative flex flex-col gap-6 px-4 sm:px-6 lg-first:px-12 min-[1575px]:px-16 py-16 lg-first:py-24 z-10 max-w-2xl lg-first:max-w-none mx-auto lg-first:mx-0">
        {/* Vertical gold line connecting cards */}
        <div
          className="hidden lg-first:block absolute left-8 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-secondary/40 to-transparent pointer-events-none"
          aria-hidden="true"
        />

        {valuesList.map((value, index) => {
          const Icon = PILLAR_ICONS[index]
          const cardRef = index === 0 ? card1Ref : index === 1 ? card2Ref : card3Ref
          const watermark = `0${index + 1}`

          return (
            <article
              key={value.id}
              ref={cardRef}
              className="our-story-value-card group relative glass-card-premium rounded-2xl p-6 sm:p-8 pl-12 lg-first:pl-14 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(223,175,55,0.2)] border border-white/10 hover:border-[#DFAF37]/40"
              role="article"
              aria-labelledby={`value-${value.id}-title`}
            >
              {/* Watermark number */}
              <span
                className="absolute top-6 right-6 text-7xl font-black text-white/5 select-none"
                aria-hidden="true"
              >
                {watermark}
              </span>
              {/* Icon */}
              <div className="relative z-10 mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-secondary/30 bg-secondary/10 text-secondary transition-transform duration-300 group-hover:scale-110">
                <Icon className="h-6 w-6" />
              </div>
              <h3
                id={`value-${value.id}-title`}
                className="font-montserrat font-bold text-xl sm:text-2xl text-white uppercase tracking-tight mb-3"
              >
                {value.title}
              </h3>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                {value.description}
              </p>
            </article>
          )
        })}
      </div>
    </section>
  )
}

export default OurStory
